import LineString from 'ol/geom/LineString';
import { Renderer } from '@/Renderer';
import router from '@/routing';
import { LineString as geojsonLineString } from 'geojson'
import { Coordinate, equals } from 'ol/coordinate';
import { RouteDelegate } from './RouteDelegate';
import GridPointHelper, { GridPointInfo } from './GridPointHelper';
import UserEventInfo from './UserEventInfo';
import SnappedRouteInfo from './SnappedRouteInfo';
import { GeometryHelpers } from './geometryhelpers';

const matchExistingRoutedPointTolerance = 1

export class RouteDelegateImp implements RouteDelegate {
    renderer: Renderer<any>
    constructor(renderer: Renderer<any>) {
        this.renderer = renderer
    }


    snapCoord(coord: Coordinate, onSuccess: (result: Coordinate) => void, onFail: (errorMessage: string) => void): void {
        const lngLatCoord = this.renderer.TransformCoordToLatLng(coord)
        router.nearest(lngLatCoord).then(nearestResponse => {
            if (nearestResponse.data.code === 'Ok' && nearestResponse.data.waypoints.length > 0) {
                const lngLatResult = nearestResponse.data.waypoints[0].location;
                const result = this.renderer.TransformCoordFromLatLng(lngLatResult)
                onSuccess(result)
            } else {
                onFail('Start point of track could not be determined - this is probably a bug, please report to admin@gpxplanner.app with screenshots if possible. Thanks')
            }
        }).catch(error => {
            console.error('snapCoord failed: ', error)
            onFail('Error determining start point of track - this is probably a network failure, please report to admin@gpxplanner.app with screenshots if possible. Thanks')
        })
    }

    private getErrorMessage(error: any): string {
        if(error.isUserError === true) {
            return error.message
        } else {
            return 'Unknown routing error - please report to admin@gpxplanner.app, preferably with screenshots.'
        }
    }

    doRoute(segment: LineString, onSuccess: (result: LineString) => void, onFail: (message: string) => void): void {
        this.doRouteAsync(segment).then((feature) => {
            onSuccess(feature)
        }).catch(error => {
            if(error.response?.data?.code === 'NoRoute') {
                onSuccess(segment) // if it returns 'no known route between these points', simply echo back the request, so it uses ATCF
            } else {
                onFail(this.getErrorMessage(error))
            }
        })
    }

    doRouteMulti(segments: LineString[], onSuccess: (result: LineString[]) => void, onFail: (message: string) => void): void {
        Promise.all(segments.map(s => this.doRouteAsync(s))).then((features) => {
            if (features) {
                if (features.length !== segments.length) {
                    console.warn("doRouteMulti: incorrect number of results")
                    onFail('Incorrect number of results: this is a bug, please report this to admin@gpxplanner.app with screenshots if possible please. Thanks')
                } else {
                    onSuccess(features)
                }
            } else {
                onFail('No features - this is a bug, please report to admin@gpxplanner.app with screenshots if possible please. Thanks')
            }
        }).catch(error => {
            console.error('doRouteMulti failed: ', error)
            onFail(this.getErrorMessage(error))
        })
    }

    async doRouteAsync(segment: LineString): Promise<LineString> {
        const routeSegmentLngLats = this.renderer.TransformLineStringToLatLng(segment)
        const routeResponse = await router.route(routeSegmentLngLats.getCoordinates())
        let trackLngLats: Coordinate[]
        if (routeResponse.data.code === 'Ok' && routeResponse.data.routes.length > 0) {
            const geoJsonLineString = routeResponse.data.routes[0].geometry as geojsonLineString
            trackLngLats = geoJsonLineString.coordinates
        } else {
            console.error('error routing, segment = ', segment, ' routeresponse = ', routeResponse)
            throw new Error('could not route')
        }
        return this.renderer.TransformLineStringFromLatLng(new LineString(trackLngLats))
    }

    isSnappable(request: LineString, response: LineString, coordinateTypeFunc: (lineString: LineString) => Coordinate) {
        const requestCoord = coordinateTypeFunc(request)
        const responseCoord = coordinateTypeFunc(response)
        const requestPixel = this.renderer.map.getPixelFromCoordinate(requestCoord)
        const responsePixel = this.renderer.map.getPixelFromCoordinate(responseCoord)
        return GeometryHelpers.isCloseEnoughToSnapToRoad(requestPixel, responsePixel)
    }

    getStartOrEndPointInfo(request: LineString, response: LineString, startOrEndEvent: UserEventInfo | undefined, coordType: (ls: LineString) => Coordinate): GridPointInfo {
        if (startOrEndEvent === undefined) {
            // this is the only branch where we must use the request as the startOrEnd point, even if we snap and introduce a 'connecting line', however far that is.
            const startOrEndMatches = this.isSnappable(request, response, coordType)
            if (startOrEndMatches) {
                return { isOnGrid: true, coords: coordType(request) } // < we have to use the request coordinate here because the startOrEnd point is fixed. However, we know it's on grid, because it matches.
            } else {
                return { isOnGrid: false, coords: coordType(request) }
                // if the startOrEnd doesn't match, then it doesn't matter if the request startOrEnd point is pixel-wise close to the response startOrEnd point
                // this is because it obviously *wasn't* pixel-wise close when it was created, i.e. when the last segment was created.
                // this is prevalent if you click in a field, then zoom out. After zooming out, point in the field is pixel-wise close to the snapped point,
                // but ATCF will have been used for the previous line, so should be used for this one.
            }
        } else {
            // we should favour the response over the request here - in the case of rerouting, we don't want to introduce 'spurs' a few metres into a field.
            // because this is in response to an event, there is no existing line, so we don't have to be 100% faithful to the request.
            return GridPointHelper.querySnap(this.renderer.map, startOrEndEvent, coordType(response))
        }
    }

    getSnappedRouteInfo(request: LineString, response: LineString, startEvent: UserEventInfo | undefined, endEvent: UserEventInfo | undefined, intendsAtcf: boolean): SnappedRouteInfo {
        // start is fixed if startEvent === undefined
        // end is fixed if endEvent === undefined

        const startPointInfo = this.getStartOrEndPointInfo(request, response, startEvent, ls => ls.getFirstCoordinate())
        const endPointInfo = this.getStartOrEndPointInfo(request, response, endEvent, ls => ls.getLastCoordinate())

        // are either point off grid? if so, use ATCF. If both on grid, use route.
        let trackLineToUse: LineString
        if ((!intendsAtcf) && startPointInfo.isOnGrid && endPointInfo.isOnGrid) {
            const responseCoords = response.getCoordinates()
            let modifiedCoords = false
            if (!equals(responseCoords[0], startPointInfo.coords)) {
                // if the start is very close, but not exact, make the first coordinate drawn the exact 
                responseCoords[0] = startPointInfo.coords
                modifiedCoords = true

            }
            if (!equals(responseCoords[responseCoords.length - 1], endPointInfo.coords)) {
                // same with the end
                responseCoords[responseCoords.length - 1] = endPointInfo.coords
                modifiedCoords = true
            }

            if (modifiedCoords) {
                response.setCoordinates(responseCoords)
            }

            trackLineToUse = new LineString(response.getCoordinates(), response.getLayout())
        } else {
            trackLineToUse = new LineString([startPointInfo.coords, endPointInfo.coords])
        }

        if (startEvent === undefined) {
            // for the line to be unbroken as per the anchors:
            if (!equals(request.getFirstCoordinate(), trackLineToUse.getFirstCoordinate())) {
                // the start of the track line must match the start coord of request, as that is where the previous (fixed) anchor is
                console.error('start coord not correct when start is fixed: request start = ', request.getFirstCoordinate(), ', trackLineToUse starts = ', trackLineToUse.getFirstCoordinate())
            }
        } else {
            // if the start is not fixed, the anchor point position we're returning must match the start of the line we're returning
            if (!equals(startPointInfo.coords, trackLineToUse.getFirstCoordinate())) {
                // the end of the track line must match where we are going to create the new anchor
                console.error('start coord not correct when start is not fixed: new start anchor = ', startPointInfo.coords, ', trackLineToUse starts = ', trackLineToUse.getFirstCoordinate())
            }
        }

        if (endEvent === undefined) {
            // if the end is fixed, the last point of the request must match the last point of the line we're returning.
            if (!equals(request.getLastCoordinate(), trackLineToUse.getLastCoordinate())) {
                console.error('end coord not correct when end is fixed: request end = ', request.getLastCoordinate(), ', trackLineToUseEnds = ', trackLineToUse.getLastCoordinate())
            }
        } else {
            // if the end is not fixed, the anchor point position we're returning must match the end of the line we're returning
            if (!equals(endPointInfo.coords, trackLineToUse.getLastCoordinate())) {
                // the end of the track line must match where we are going to create the new anchor
                console.error('end coord not correct when end is not fixed: new end anchor = ', endPointInfo.coords, ', trackLineToUse ends = ', trackLineToUse.getLastCoordinate())
            }
        }

        return {
            endPointToUse: endPointInfo.coords,
            startPointToUse: startPointInfo.coords,
            trackLineToUse: trackLineToUse
        }
    }
}   